<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import moment from 'moment'
import { DEFAULT_DATETIME_PICKER_HEIGHT, ISO_DATE_FORMAT } from '@/utils/constants'
import { useCalendar, DAYS_IN_WEEK, MONTHS_IN_YEAR } from '@/hooks'
import BaseInputField from './BaseInputField.vue'
import BaseButton from './BaseButton.vue'
import BaseMenuLite from './BaseMenuLite.vue'

const props = defineProps<{
  noRange?: boolean
  datetime?: boolean
  color?: string
  hideLabel?: boolean
  showTime?: boolean
  disabledApply?: boolean
  disableFutureDates?: boolean
  dateTimeFormat?: string
}>()

const boundModel = defineModel({ default: new Date() })
const emit = defineEmits(['input', 'change'])

const calendar = useCalendar()
const {
  activeMonth,
  activeYear,
  startTimestamp,
  endTimestamp,
  activeMonthDatesToDisplay,
  showPreviousMonth,
  showNextMonth,
  shouldHighlight,
  getSelectionClass,
  handleMouseOver,
  getDateTimestamp,
  isFutureDate,
  currentDate
} = calendar

const isCalendarOpen = ref(false)
const datepickerActivatorElement = ref()
const datepickerCalendar = ref()
const positionCalendarToTopOfActivator = ref(false)
const computedCalendarStyle = ref({})
const selectedHour = ref('')
const selectedMinute = ref('')

const getHoursAndMinutesInMiliseconds = () => {
  const hoursToAdd = parseInt(selectedHour.value, 10)
  const minutesToAdd = parseInt(selectedMinute.value, 10)
  const toMilliseconds = (hoursToAdd * 60 * 60 + minutesToAdd * 60) * 1000
  return toMilliseconds
}

const selectedDateTimeRange = computed(() => {
  let formattedStartDate = ''
  let formattedEndDate = ''
  const dateFormat = props.datetime ? 'DD MMM YYYY HH:mm' : 'DD MMM YYYY'
  if (startTimestamp.value) {
    formattedStartDate = moment(startTimestamp.value).format(dateFormat)
  }
  if (endTimestamp.value) {
    formattedEndDate = moment(endTimestamp.value).format(dateFormat)
  }

  const dateRangeText = `${formattedStartDate} ${formattedEndDate ? '-' : ''} ${formattedEndDate}`
  if (dateRangeText.trim()) return dateRangeText
  return ''
})

const monthsInYearOptions = (() => {
  return MONTHS_IN_YEAR.map((month, index) => ({
    label: month,
    value: index,
    textClass: 'px-1 py-1',
    action: () => {
      activeMonth.value = index
    }
  }))
})()

const yearOptions = (() => {
  const years = []
  for (let i = activeYear.value - 25; i <= activeYear.value + 25; i++) {
    years.push({
      label: i.toString(),
      value: i,
      textClass: 'px-1 py-1',
      action: () => {
        activeYear.value = i
      }
    })
  }
  return years
})()

const updateSelectedDateTime = () => {
  let endTimestampTemp = endTimestamp.value

  if (!props.noRange && !props.showTime) {
    endTimestampTemp = (endTimestampTemp ?? 0) + (23 * 3600 + 59 * 60) * 1000
  }

  const newStartTimestamp = moment(startTimestamp.value).format(
    props.dateTimeFormat ?? ISO_DATE_FORMAT
  )

  if (startTimestamp.value) {
    boundModel.value = new Date(newStartTimestamp)
  }
  emit('change')
}

const handleCurrentMonthDateClick = (event: Event) => {
  const dateElement = event.currentTarget as HTMLButtonElement
  if (!props.noRange) {
    if (endTimestamp.value) {
      startTimestamp.value = parseInt(dateElement.value, 10)
      endTimestamp.value = null
      return
    }
    if (startTimestamp.value && startTimestamp.value < parseInt(dateElement.value, 10)) {
      endTimestamp.value = parseInt(dateElement.value, 10)
      return
    }
  }
  startTimestamp.value = parseInt(dateElement.value, 10)
  selectedHour.value = moment(startTimestamp.value).format('HH')
  selectedMinute.value = moment(startTimestamp.value).format('mm')

  if (props.noRange) updateSelectedDateTime()
  nextTick(() => {
    isCalendarOpen.value = false
  })
}

const closeDateTimePicker = (event: MouseEvent) => {
  if (isCalendarOpen.value) {
    const clickOrigin = event.target as HTMLElement
    if (clickOrigin?.closest('#datepicker-calendar') || clickOrigin?.closest('.datetime-input'))
      return
    isCalendarOpen.value = !isCalendarOpen.value
  }
}

const resetDatepicker = () => {
  startTimestamp.value = null
  endTimestamp.value = null

  if (props.noRange) updateSelectedDateTime()
}

const setStartAndEndDates = () => {
  if (boundModel.value) startTimestamp.value = moment(boundModel.value).toDate().getTime()
  selectedHour.value = moment(startTimestamp.value).format('HH')
  selectedMinute.value = moment(startTimestamp.value).format('mm')
}

const removeOutsideCalendarClickListener = () => {
  document.removeEventListener('click', closeDateTimePicker)
}

const isToday = (date: number) => {
  const startOfDay = moment(date).startOf('day')
  const currentDateStartOfDay = moment(currentDate).startOf('day')
  return startOfDay.valueOf() === currentDateStartOfDay.valueOf()
}

const updateTimeStamp = () => {
  const timeStampWithDateTime = startTimestamp.value

  const dateWithTime = new Date(timeStampWithDateTime ?? 0)
  dateWithTime.setHours(0, 0, 0, 0)
  const timeStampWithoutTime = dateWithTime.getTime()

  startTimestamp.value = 0
  startTimestamp.value = timeStampWithoutTime + getHoursAndMinutesInMiliseconds()

  if (props.noRange) updateSelectedDateTime()
}

const disabledNextdates = (date: number) => {
  if (props.disableFutureDates && isFutureDate(date)) {
    return 'disabled'
  }
  return ''
}

const closeDatePickerOnOutsideClick = (e: PointerEvent) => {
  const target = e.target as HTMLElement
  if (target) {
    const wasMenuOptionClick = target.closest('.base-menu--options')
    if (wasMenuOptionClick) return
  }
  isCalendarOpen.value = false
}

watch(isCalendarOpen, () => {
  nextTick(() => {
    const inputFieldElement = datepickerActivatorElement.value
    const calendarElementHeight = datepickerCalendar.value?.$el.clientHeight

    const { top, bottom, left } = inputFieldElement.getBoundingClientRect()
    const inputFieldElementHeight = bottom - top

    const distanceFromWindowBottom = window.innerHeight - top

    let calendarPosition: any = {
      left: `${left}px`
    }

    if (distanceFromWindowBottom < calendarElementHeight + inputFieldElementHeight) {
      positionCalendarToTopOfActivator.value = true
      calendarPosition = {
        ...calendarPosition,
        top: `${top - calendarElementHeight}px`
      }
    } else {
      positionCalendarToTopOfActivator.value = false
      calendarPosition = {
        ...calendarPosition,
        top: `${bottom}px`
      }
    }
    computedCalendarStyle.value = calendarPosition
    resetToSelectedDate()
  })
})

const resetToSelectedDate = () => {
  if (boundModel.value) {
    activeMonth.value = moment(boundModel.value).month()
    activeYear.value = moment(boundModel.value).year()
  }
}

onMounted(() => {
  setStartAndEndDates()
})

onUnmounted(() => {
  removeOutsideCalendarClickListener()
})

defineExpose({ resetDatepicker })
</script>
<template>
  <div class="datepicker">
    <div ref="datepickerActivatorElement">
      <slot name="default" :on="{ click: () => (isCalendarOpen = true) }">
        <BaseInputField
          v-model="selectedDateTimeRange"
          rounded
          :outlined="$attrs.plain === undefined"
          readonly
          :height="$attrs.height ?? DEFAULT_DATETIME_PICKER_HEIGHT"
          :class="`datetime-input ${$attrs.small !== undefined ? 'body-3 medium' : 'inherit--font'}`"
          :show-highlight="Boolean(selectedDateTimeRange)"
          :placeholder="
            Boolean(showTime)
              ? `Select date and time ${noRange ? '' : 'range'}`
              : `Select date ${noRange ? '' : 'range'}`
          "
          hide-details
          @click="() => (isCalendarOpen = true)"
        >
          <template #append>
            <v-icon
              :size="$attrs.small !== undefined ? 14 : 16"
              :color="selectedDateTimeRange ? 'brand_light' : ''"
            >
              mdi-calendar-month
            </v-icon>
          </template>
        </BaseInputField>
      </slot>
    </div>
    <Transition name="slide-y-reverse-transition">
      <Teleport to="body">
        <v-card
          v-if="isCalendarOpen"
          ref="datepickerCalendar"
          id="datepicker-calendar"
          :right="$attrs['align-calendar'] === 'right'"
          class="calendar--active border-gray rounded-lg"
          max-width="340"
          :style="computedCalendarStyle"
          v-click-outside="closeDatePickerOnOutsideClick"
        >
          <v-card-title class="d-flex align-center black--text body-1 semibold pb-3">
            <v-icon @click="showPreviousMonth"> mdi-chevron-left </v-icon>
            <BaseMenuLite
              class="ml-auto"
              content-class="body-1--new restrict-height"
              :items="monthsInYearOptions"
              v-model="activeMonth"
            >
              <template #menu-activator="{ on }">
                <BaseButton
                  variant="outlined"
                  height-variant="sm"
                  class="ml-auto pl-3 pr-2 py-0 border-gray"
                  v-on="on"
                >
                  {{ MONTHS_IN_YEAR[activeMonth]
                  }}<v-icon class="ml-2" size="18" icon="mdi-chevron-down" />
                </BaseButton>
              </template>
            </BaseMenuLite>
            &nbsp;
            <BaseMenuLite
              class="mr-auto"
              content-class="body-1--new restrict-height"
              :items="yearOptions"
              v-model="activeYear"
            >
              <template #menu-activator="{ on }">
                <BaseButton
                  variant="outlined"
                  height-variant="sm"
                  class="mr-auto pl-3 pr-2 py-0 border-gray"
                  v-on="on"
                >
                  {{ activeYear }}<v-icon class="ml-2" size="18" icon="mdi-chevron-down" />
                </BaseButton>
              </template>
            </BaseMenuLite>
            <v-icon @click="showNextMonth"> mdi-chevron-right </v-icon>
          </v-card-title>
          <v-divider />
          <v-card-subtitle class="d-flex weekday-labels py-3 secondary_text--text caps-12 regular">
            <span
              v-for="(day, index) in DAYS_IN_WEEK"
              :key="index"
              class="datepicker-item text-center"
              >{{ day }}</span
            >
          </v-card-subtitle>
          <v-card-text class="datepicker-content d-flex flex-wrap">
            <button
              v-for="(date, index) in activeMonthDatesToDisplay.previousMonthDates"
              :key="`previous-month-${index}`"
              class="datepicker-item d-inline-block text-center body-2 regular black--text disabled"
            >
              {{ date }}
            </button>
            <button
              v-for="(date, index) in activeMonthDatesToDisplay.currentMonthDates"
              :key="index"
              :class="`
                ${getSelectionClass(date, noRange)}
                ${shouldHighlight(date, noRange)} 
                ${disabledNextdates(date)}
                ${noRange || !endTimestamp ? 'no-range' : ''} 
                datepicker-item d-inline-block text-center body-2 regular black--text`"
              :value="getDateTimestamp(date)"
              :today="isToday(getDateTimestamp(date))"
              @click="handleCurrentMonthDateClick"
              @mouseenter="handleMouseOver"
              @mouseleave="handleMouseOver"
              @focusin="handleMouseOver"
              @focusout="handleMouseOver"
            >
              <span class="date-wrapper">{{ date }}</span>
            </button>
            <button
              v-for="(date, index) in activeMonthDatesToDisplay.nextMonthDates"
              :key="`next-month-${index}`"
              class="datepicker-item d-inline-block text-center body-2 regular black--text disabled"
            >
              {{ date }}
            </button>
          </v-card-text>
        </v-card>
      </Teleport>
    </Transition>
  </div>
</template>
